import React, { PropsWithChildren, useContext, useMemo, useRef } from "react";
import { ApiContext, ApiContextProvider } from "@root/api/ApiContext";
import { LineupEditAction, LineupID } from "../types";
import { ActionEdit, ActionGet, ActionGetOptions } from "../actions";

export interface ILineupApiContext {
  action(actions: LineupEditAction[]): Promise<ActionEdit["Resp"]>;
  get(withPickedOptions: boolean): Promise<ActionGet["Resp"]>;
  getOptions(): Promise<ActionGetOptions["Resp"]>;
}

export const LineupApiContext = React.createContext<ILineupApiContext>({
  // broken fallback on purpose
  action: () => ({} as any),
  get: () => ({} as any),
  getOptions: () => ({} as any),
});

const LineupApiProviderInner: React.FC<
  React.PropsWithChildren<{ lineupId: LineupID }>
> = ({ children, lineupId }) => {
  const api = useContext(ApiContext);
  const lineupIdRef = useRef(lineupId);
  lineupIdRef.current = lineupId;

  const value: ILineupApiContext = useMemo(() => {
    return {
      get: (withPickedOptions) =>
        api.call<ActionGet>("/get", {
          id: lineupIdRef.current,
          with_picked_options: withPickedOptions,
        }),
      action: (actions) =>
        api.call<ActionEdit>("/edit", { id: lineupIdRef.current, actions }),
      getOptions: () =>
        api.call<ActionGetOptions>("/get_options", { id: lineupIdRef.current }),
    };
  }, [api]);

  return (
    <LineupApiContext.Provider value={value}>
      {children}
    </LineupApiContext.Provider>
  );
};

export const LineupApiProvider: React.FC<
  React.PropsWithChildren<{ authToken: string; lineupId: LineupID }>
> = ({ children, authToken, lineupId }) => {
  return (
    <ApiContextProvider authToken={authToken} prefix="/api/lineup">
      <LineupApiProviderInner lineupId={lineupId}>
        {children}
      </LineupApiProviderInner>
    </ApiContextProvider>
  );
};
